// Pass 5.2 — pure filter+sort over /system/status workspace rows.
// Lives outside the component so it's trivially unit-testable;
// the component just calls filterAndSortWorkspaces() in render.

import type { SystemStatusWorkspace, DriveQueueDepth } from "./api";
import { workspaceHealthColor, type HealthColor } from "./workspaceHealth";
import type { StatusFilterKind, StatusSortKind } from "./statusListPrefs";

// Severity rank for the "broken first" sort: red > yellow > gray > green.
const SEVERITY_RANK: Record<HealthColor, number> = {
  red: 0,
  yellow: 1,
  gray: 2,
  green: 3,
};

export interface FilterAndSortArgs {
  workspaces: SystemStatusWorkspace[];
  driveQueue: DriveQueueDepth | null;
  search: string;
  status: StatusFilterKind;
  sort: StatusSortKind;
  /** Optional for test determinism. Production omits. */
  now?: number;
}

export function filterAndSortWorkspaces({
  workspaces,
  driveQueue,
  search,
  status,
  sort,
  now,
}: FilterAndSortArgs): SystemStatusWorkspace[] {
  const needle = search.trim().toLowerCase();

  const matchesSearch = (w: SystemStatusWorkspace) =>
    !needle ||
    w.team_name.toLowerCase().includes(needle) ||
    w.team_id.toLowerCase().includes(needle);

  const matchesStatus = (w: SystemStatusWorkspace) =>
    status === "all" || w.vault_status === status;

  const filtered = workspaces.filter((w) => matchesSearch(w) && matchesStatus(w));

  // Sort. Copy first — never mutate the source array.
  const sorted = filtered.slice();
  switch (sort) {
    case "name":
      sorted.sort((a, b) => a.team_name.localeCompare(b.team_name));
      break;
    case "polled":
      // Stalest poll first. Null = "never polled" → sorts to top
      // (more concerning than any timestamp).
      sorted.sort((a, b) => pollAge(b, now) - pollAge(a, now));
      break;
    case "last_message":
      // Most recent message first. Null = "no messages ever" sorts
      // to the bottom (least interesting).
      sorted.sort((a, b) => lastMsgEpoch(b) - lastMsgEpoch(a));
      break;
    case "severity":
    default:
      // Most-broken first; tiebreak on stalest poll so within the
      // "all green" bucket the operator still sees what was polled
      // longest ago.
      sorted.sort((a, b) => {
        const colorA = workspaceHealthColor({ workspace: a, driveQueue, now });
        const colorB = workspaceHealthColor({ workspace: b, driveQueue, now });
        const sevDiff = SEVERITY_RANK[colorA] - SEVERITY_RANK[colorB];
        if (sevDiff !== 0) return sevDiff;
        return pollAge(b, now) - pollAge(a, now);
      });
      break;
  }
  return sorted;
}

function pollAge(w: SystemStatusWorkspace, now: number | undefined): number {
  // Returns "ms since last poll". Null last_polled_at returns
  // Infinity so it sorts as "stalest" — matches the operator
  // intuition that a never-polled workspace is the most
  // concerning by this dimension.
  if (!w.last_polled_at) return Number.POSITIVE_INFINITY;
  const t = Date.parse(w.last_polled_at);
  if (Number.isNaN(t)) return Number.POSITIVE_INFINITY;
  return (now ?? Date.now()) - t;
}

function lastMsgEpoch(w: SystemStatusWorkspace): number {
  // Returns "ms epoch of last message" or -Infinity for null /
  // unparsable, so those sort LAST under "most recent first."
  if (!w.last_message_at) return Number.NEGATIVE_INFINITY;
  const t = Date.parse(w.last_message_at);
  if (Number.isNaN(t)) return Number.NEGATIVE_INFINITY;
  return t;
}
